/**
 * Message shown when a user tries to act on an AI Insights Template that belongs
 * to another account. The template can still be listed (a shared saved report may
 * feed an Insight in another account), but Edit/Delete/Toggle must be blocked and
 * the user steered to the owning account, where the wizard's data-source context
 * resolves correctly.
 */
export const FOREIGN_TEMPLATE_MESSAGE =
    'This AI Insights Template belongs to another account. Switch to that account to manage it.';

/**
 * The blocked-action message, naming the owning account when the API supplied it.
 *
 * Falls back to the generic wording when `accountName` is absent (an account that
 * was deleted, or a response served before the name was added), so the user always
 * learns why the action was blocked even if we can't say where to go.
 *
 * The name is display only — ownership is decided by `isForeignTemplate` on
 * `accountId`, never by this string.
 *
 * @param {{ accountName?: string }} template
 * @returns {string}
 */
export const foreignTemplateMessage = (template) =>
    template && template.accountName
        ? `This AI Insights Template belongs to the account "${template.accountName}". Switch to that account to manage it.`
        : FOREIGN_TEMPLATE_MESSAGE;

/**
 * Whether a template belongs to an account other than the one the user is acting in.
 *
 * Fails open (returns false) when either id is missing, so incomplete data never
 * blocks a legitimate own-account action — an own-account template always has
 * `accountId === activeAccountId`.
 *
 * @param {{ accountId?: string }} template
 * @param {string} activeAccountId
 * @returns {boolean}
 */
export const isForeignTemplate = (template, activeAccountId) => {
    if (!template || !template.accountId || !activeAccountId) {
        return false;
    }
    return template.accountId !== activeAccountId;
};
